import React, { useEffect, useState, useMemo, useRef } from "react";
import { RightOutlined, LockOutlined, UserOutlined, LogoutOutlined, InfoCircleOutlined, NumberOutlined } from "@ant-design/icons";
import {
  SidebarWrapper,
  SidebarHeader,
  ThemeLogo,
  MenuScrollArea,
  MenuWrapper,
  MenuScrollTrack,
  MenuScrollThumb,
  MenuItem,
  MenuIcon,
  MenuLabel,
  MenuBadge,
  ToggleSwitchWrap,
  ToggleSwitch,
  CollapsedMenuItem,
  CollapsedMenuIcon,
  CollapsedBadge,
  BottomProfileWrapper,
  BottomProfileButton,
  BottomProfileInfo,
  BottomProfileName,
  ProfileDropdownMenu,
  ProfileDropdownItem,
  ProfileDropdownDivider,
  CollapsedBottomProfile,
  PoweredByWrapper,
  PoweredByBadge,
  VersionWrapper,
  VersionDivider,
  VersionText,
} from "./SidebarStyle";
import { Tooltip, Popconfirm } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSidebarMenu, getUserData, isMultiOrgUser, TOPLINE_COUNT_EVENT } from "../../utils/permissions";
import { refreshToplineCountApi } from "../../store/actions/projectAction";
import { useDispatch, useSelector } from "react-redux";
import { logoutApi } from "../../store/actions/loginAction";
import { PopconfirmGlobalStyles } from "../../pages/Surveys/Create/QuestionPreview.style";
import InitialsAvatar from "../InitialsAvatar/InitialsAvatar";
import OrgSwitcher from "../OrgSwitcher/OrgSwitcher";
import ProfileEditModal from "../ProfileModals/ProfileEditModal";
import ChangePasswordModal from "../ProfileModals/ChangePasswordModal";

const Sidebar = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isProfileEditModalOpen, setIsProfileEditModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  // Topline shows in the menu only while it holds a summary this person can
  // see: count it on load, and rebuild the menu whenever the count changes.
  const [toplineTick, setToplineTick] = useState(0);
  useEffect(() => {
    const onCount = () => setToplineTick((t) => t + 1);
    window.addEventListener(TOPLINE_COUNT_EVENT, onCount);
    refreshToplineCountApi();
    return () => window.removeEventListener(TOPLINE_COUNT_EVENT, onCount);
  }, []);
  const profileMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = getUserData();
  const userName = userData?.name || "User";
  const orgLogo = useSelector(state => state.login?.user?.organization?.logo);
  const appVersion = localStorage.getItem('app_version') || null;

  // Org-level visibility settings (default: show both)
  const orgSettings = (() => {
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}');
      return {
        showPoweredBy: ud?.organization?.show_powered_by !== false,
        showVersion: ud?.organization?.show_version !== false,
      };
    } catch (e) { return { showPoweredBy: true, showVersion: true }; }
  })();

  const [orgLogoUrl, setOrgLogoUrl] = useState(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return userData?.organization?.logo?.downloadUrl || null;
    } catch (e) { return null; }
  });
  const [orgLogoIsSquare, setOrgLogoIsSquare] = useState(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return userData?.organization?.logo?.is_square === true;
    } catch (e) { return false; }
  });
  const [logoError, setLogoError] = useState(false);

  // Sync from Redux only when it has actual data (ignore reset/null transients)
  useEffect(() => {
    if (orgLogo?.downloadUrl) {
      setOrgLogoUrl(orgLogo.downloadUrl);
      setOrgLogoIsSquare(orgLogo.is_square === true);
      setLogoError(false);
    } else if (orgLogo === null || orgLogo === undefined) {
      // Only clear if there's truly no org logo (not just a Redux reset)
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const lsUrl = userData?.organization?.logo?.downloadUrl || null;
        setOrgLogoUrl(lsUrl);
        setOrgLogoIsSquare(userData?.organization?.logo?.is_square === true);
      } catch (e) { setOrgLogoUrl(null); setOrgLogoIsSquare(false); }
    }
  }, [orgLogo]);


  // Collapsed state is owned by AppLayout (App.js): it seeds from localStorage,
  // auto-collapses below COMPACT_BREAKPOINT, and persists the desktop preference.
  // Persisting here too would overwrite that preference on every auto-collapse.

  // Function to get the current selected key based on the route
  const getCurrentSelectedKey = () => {
    const routeMap = {
      "/": "dashboard",
      "/topline/": "topline",
      "/genz-signals/": "genz-signals",
      "/nyc-signals/": "nyc-signals",
      "/gen-z-dashboard/": "genz-dashboard",
      "/vitalcity/": "vitalcity-dashboard",
      "/vitalcity-new/": "vitalcity-dashboard",
      "/vitalcity-discontinued/": "vitalcity-dashboard",
      "/projects/": "projects",
      "/insight-engine/": "insights",
      "/surveys/": "survey",
      "/polling-agents/": "aijdv",
      "/campaigns/": "campaigns",
      "/manage": "manage",
      "/organizations": "organizations",
      "/user-tracking": "user-tracking",
      "/all-organizations": "all-organizations",
    };

    // The map keys carry a trailing slash but the address bar often does not
    // (/vitalcity vs /vitalcity/), which left no item highlighted (user
    // 2026-09-11). Match on the slash-terminated form.
    const pathname = location.pathname.endsWith("/") ? location.pathname : `${location.pathname}/`;

    // Check for exact match first
    if (routeMap[pathname]) {
      return routeMap[pathname];
    }

    // Check for partial matches (e.g., /projects/123 should match /projects/)
    for (const [route, key] of Object.entries(routeMap)) {
      if (pathname.startsWith(route) && route !== "/") {
        return key;
      }
    }

    // Default to Dashboard if no match found
    return "dashboard";
  };

  const handleMenuClick = (route) => {
    navigate(route);
  };

  const handleLogout = () => {
    dispatch(logoutApi());
    navigate("/login");
  };

  const currentSelected = getCurrentSelectedKey();

  // Get menu items from centralized permission system
  // This reads from ROUTES config in permissions.js and filters based on user permissions
  const menuItems = useMemo(() => {
    const sidebarRoutes = getSidebarMenu();
    // Transform ROUTES format to menu item format
    return sidebarRoutes.map((route) => ({
      key: route.key,
      label: route.label,
      description: route.description,
      icon: route.icon,
      route: route.path,
      badge: route.badge,
    }));
  }, [toplineTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expanded content only when sidebar is open (no expand on hover — open only via toggle click)
  const shouldShowExpanded = !props.collapsed;

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleProfileClick = () => {
    setIsProfileMenuOpen(false);
    setIsProfileEditModalOpen(true);
  };

  const handleChangePasswordClick = () => {
    setIsProfileMenuOpen(false);
    setIsChangePasswordModalOpen(true);
  };

  /* Custom scroll indicator for the menu (see MenuScrollTrack/Thumb):
     the native bar is an overlay one on macOS, so it neither reserves space
     nor stays visible. Recomputed on scroll, on resize and whenever the item
     list or collapsed state changes. */
  const menuRef = useRef(null);
  const [scrollThumb, setScrollThumb] = useState({ visible: false, top: 0, height: 0 });
  const updateScrollThumb = React.useCallback(() => {
    const el = menuRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight + 1) {
      setScrollThumb((prev) => (prev.visible ? { visible: false, top: 0, height: 0 } : prev));
      return;
    }
    const height = Math.max(28, Math.round((clientHeight / scrollHeight) * clientHeight));
    const top = Math.round((scrollTop / (scrollHeight - clientHeight)) * (clientHeight - height));
    setScrollThumb((prev) =>
      prev.visible && prev.top === top && prev.height === height
        ? prev
        : { visible: true, top, height }
    );
  }, []);
  // Observer once: fires when the window (and so the menu) changes height.
  useEffect(() => {
    const el = menuRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(updateScrollThumb);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScrollThumb]);
  // Every render: the item list itself can change (permissions arriving, the
  // collapse toggle) without the element resizing. Cheap, and it cannot loop -
  // updateScrollThumb keeps the previous state object when nothing moved.
  useEffect(() => {
    updateScrollThumb();
  });

  return (
    <SidebarWrapper
      className="appSidebar"
      collapsed={props.collapsed}
      isHovered={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PopconfirmGlobalStyles />
      {/* Header: when open = full logo + toggle; when closed = small logo, toggle overlays logo on hover */}
      <SidebarHeader collapsed={props.collapsed}>
        <ThemeLogo collapsed={props.collapsed}>
          <Link to="/">
            {orgLogoUrl && !logoError ? (
              <img
                src={orgLogoUrl}
                alt="Logo"
                style={
                  props.collapsed
                    ? { maxHeight: 28, objectFit: 'contain' }
                    : orgLogoIsSquare
                      ? { height: 60, width: 60, objectFit: 'contain' }
                      : { maxHeight: 36, objectFit: 'contain' }
                }
                onError={() => setLogoError(true)}
              />
            ) : props.collapsed ? (
              <img src="/favicon.ico" alt="CrowdVox" />
            ) : (
              <img src="/images/logo.svg" alt="CrowdVox" />
            )}
          </Link>
        </ThemeLogo>
        <ToggleSwitchWrap collapsed={props.collapsed} isHovered={isHovered}>
          <Tooltip title={props.collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
            <ToggleSwitch collapsed={props.collapsed}>
              <img
                src="/images/switch-sidebar.svg"
                alt={props.collapsed ? "Expand sidebar" : "Collapse sidebar"}
                onClick={(e) => {
                  e.stopPropagation();
                  props.toggle(!props.collapsed);
                }}
              />
            </ToggleSwitch>
          </Tooltip>
        </ToggleSwitchWrap>
      </SidebarHeader>


      {/* Active organization indicator + switcher. Renders nothing unless the
          logged-in user has multi-org access, so single-org chrome is unchanged. */}
      {isMultiOrgUser() && (
        <div style={{ paddingRight: shouldShowExpanded ? 12 : 0 }}>
          <OrgSwitcher collapsed={!shouldShowExpanded} />
        </div>
      )}

      {/* Navigation Menu */}
      <MenuScrollArea>
      <MenuWrapper ref={menuRef} $collapsed={props.collapsed} onScroll={updateScrollThumb}>
        {shouldShowExpanded
          ? // Expanded view - show full menu items (either permanently expanded or hover-expanded)
            menuItems.map((item) => (
              <MenuItem
                key={item.key}
                active={currentSelected === item.key}
                onClick={() => handleMenuClick(item.route)}
              >
                <MenuIcon active={currentSelected === item.key}>
                  <img src={item.icon} alt={item.label} />
                </MenuIcon>
                <MenuLabel>{item.label}</MenuLabel>
                {item.description && (
                  <Tooltip title={item.description} placement="right">
                    <InfoCircleOutlined
                      style={{
                        fontSize: 12,
                        color: '#9CA3AF',
                        marginLeft: 'auto',
                        opacity: 0.7,
                        cursor: 'help'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                )}
                {item.badge && <MenuBadge>{item.badge}</MenuBadge>}
              </MenuItem>
            ))
          : // Collapsed view - show only icons with tooltips
            menuItems.map((item) => (
              <Tooltip key={item.key} title={item.label} placement="right">
                <CollapsedMenuItem
                  active={currentSelected === item.key}
                  onClick={() => handleMenuClick(item.route)}
                >
                  <CollapsedMenuIcon active={currentSelected === item.key}>
                    <img src={item.icon} alt={item.label} />
                  </CollapsedMenuIcon>
                  {item.badge && <CollapsedBadge>{item.badge}</CollapsedBadge>}
                </CollapsedMenuItem>
              </Tooltip>
            ))}
      </MenuWrapper>
      {scrollThumb.visible && (
        <>
          <MenuScrollTrack />
          <MenuScrollThumb style={{ top: scrollThumb.top, height: scrollThumb.height }} />
        </>
      )}
      </MenuScrollArea>

      {/* ChatGPT-style Bottom Profile Menu */}
      {shouldShowExpanded ? (
        <BottomProfileWrapper ref={profileMenuRef}>
          {/* Dropdown Menu */}
          <ProfileDropdownMenu isOpen={isProfileMenuOpen}>
            <ProfileDropdownItem onClick={handleProfileClick}>
              <UserOutlined />
              Edit Profile
            </ProfileDropdownItem>
            <ProfileDropdownItem onClick={handleChangePasswordClick}>
              <LockOutlined />
              Change Password
            </ProfileDropdownItem>
            <ProfileDropdownDivider />
            <Popconfirm
              title="Confirm Logout"
              description="Are you sure you want to logout?"
              okText="Yes"
              cancelText="No"
              onConfirm={handleLogout}
              placement="right"
            >
              <ProfileDropdownItem className="danger">
                <LogoutOutlined />
                Logout
              </ProfileDropdownItem>
            </Popconfirm>
          </ProfileDropdownMenu>

          {/* Profile Button */}
          <BottomProfileButton isOpen={isProfileMenuOpen} onClick={toggleProfileMenu}>
            <InitialsAvatar name={userName} size={36} />
            <BottomProfileInfo>
              <BottomProfileName>{userName}</BottomProfileName>
            </BottomProfileInfo>
            <RightOutlined style={{ color: "#6D6A67", fontSize: "12px", transform: isProfileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
          </BottomProfileButton>
        </BottomProfileWrapper>
      ) : (
        // Collapsed view - show avatar with tooltip and dropdown (always visible when closed, not hidden on hover)
        props.collapsed && (
          <BottomProfileWrapper ref={profileMenuRef}>
            <ProfileDropdownMenu isOpen={isProfileMenuOpen}>
              <ProfileDropdownItem onClick={handleProfileClick}>
                <UserOutlined />
                Edit Profile
              </ProfileDropdownItem>
              <ProfileDropdownItem onClick={handleChangePasswordClick}>
                <LockOutlined />
                Change Password
              </ProfileDropdownItem>
              <ProfileDropdownDivider />
              <Popconfirm
                title="Confirm Logout"
                description="Are you sure you want to logout?"
                okText="Yes"
                cancelText="No"
                onConfirm={handleLogout}
                placement="right"
              >
                <ProfileDropdownItem className="danger">
                  <LogoutOutlined />
                  Logout
                </ProfileDropdownItem>
              </Popconfirm>
            </ProfileDropdownMenu>
            <Tooltip title={userName} placement="right">
              <CollapsedBottomProfile onClick={toggleProfileMenu}>
                <InitialsAvatar name={userName} size={28} />
              </CollapsedBottomProfile>
            </Tooltip>
          </BottomProfileWrapper>
        )
      )}

      {/* Powered By CrowdVox - below profile */}
      {orgSettings.showPoweredBy && shouldShowExpanded && (
        <PoweredByWrapper>
          <PoweredByBadge>
            <img src="/images/crowdvox-logo-purple.svg" alt="CrowdVox" />
          </PoweredByBadge>
        </PoweredByWrapper>
      )}
      {orgSettings.showPoweredBy && props.collapsed && (
        <Tooltip title="CrowdVox" placement="right">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, margin: '0 auto 4px', cursor: 'default' }}>
            <img src="/favicon.ico" alt="CrowdVox" style={{ width: 20, height: 20, opacity: 0.45 }} />
          </div>
        </Tooltip>
      )}

      {/* Version Number - below powered by */}
      {orgSettings.showVersion && appVersion && shouldShowExpanded && (
        <VersionWrapper>
          <VersionText>Version {appVersion}</VersionText>
        </VersionWrapper>
      )}
      {orgSettings.showVersion && appVersion && props.collapsed && (
        <Tooltip title={`Version ${appVersion}`} placement="right">
          <VersionWrapper style={{ textAlign: 'center', padding: '4px 0' }}>
            <NumberOutlined style={{ fontSize: 12, color: '#9CA3AF', opacity: 0.5 }} />
          </VersionWrapper>
        </Tooltip>
      )}

      {/* Profile Edit Modal */}
      <ProfileEditModal
        open={isProfileEditModalOpen}
        onClose={() => setIsProfileEditModalOpen(false)}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </SidebarWrapper>
  );
};

export default Sidebar;
